import * as Enumerable from 'linq';
import { DateTime } from 'luxon';
import { map, Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, ViewChild, ViewEncapsulation, ViewChildren, QueryList } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, DateSelectArg, DateSpanApi, EventChangeArg } from '@fullcalendar/core';
import enAuLocale from '@fullcalendar/core/locales/en-au';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin, { EventResizeDoneArg } from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon3';
import resourceDayGridPlugin from '@fullcalendar/resource-daygrid';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import { AccountEntity, Allocation, DateUtility, SchedulingSettings, Source } from '@jms/model';
import { AllocationEventData, SchedulerContactSummary } from '@jms/shared/module.scheduling.allocations.pro';
import { PickerFetch } from '@jms/ui/ui.core.pickers';
import { ObservableInput } from '@jms/utils/util.redux';
import { StateComponent } from '@jms/utils/util.state.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { APPOINTMENT_SERVICE, AppointmentEditorContext, AppointmentsService } from '../../../common';
import { SelectDateTimeRange } from '../../model';

interface AppointmentCalendarComponentVM {
	calendarOptions: CalendarOptions;
	timezone: string;
	resources: AccountEntity[];
	selectedAllocation: Allocation;
	source: Source;
}


const initialState: AppointmentCalendarComponentVM = {
	calendarOptions: null,
	timezone: null,
	resources: [],
	selectedAllocation: null,
	source: null,
};

@UntilDestroy()
@Component({
	selector: 'appointment-calendar',
	templateUrl: './appointment.calendar.component.html',
	styleUrls: ['./appointment.calendar.component.less'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [],
	standalone: false
})
export class AppointmentCalendarComponent extends StateComponent<AppointmentCalendarComponentVM> implements AfterViewInit {


	@ObservableInput() @Input('context') public context$: Observable<AppointmentEditorContext>;

	@Output() public selectTimeRange = new EventEmitter<SelectDateTimeRange>();

	@ViewChild('calendar') public calendarComponent: FullCalendarComponent;
	@ViewChildren('calendar') public calendars: QueryList<FullCalendarComponent>;

	public calendarOptions: CalendarOptions;
	public showCalendar = false;
	public resourceFilterControl = new FormControl();

	public readonly PickerFetch = PickerFetch;

	private _revertChangeAllocationIds: string[] = [];

	constructor(
		@Inject(APPOINTMENT_SERVICE) private appointmentsService: AppointmentsService,
		private cdr: ChangeDetectorRef
	) {

		super(initialState);

		this.context$
			.pipe(
				untilDestroyed(this),
				filter(context => !!context),
				map(context => {
					const hours = this.getHours(context.settings?.schedule);
					return { hours, ...context }
				}),
				tap((context) => {
					const { hours, timezone, resources, appointment, source } = context;

					const calendarOptions = this.getCalendarOptions(resources, timezone, hours, appointment);
					this.setStateDirect({ calendarOptions, timezone, resources, selectedAllocation: appointment, source });
				})

			).subscribe();


		this.resourceFilterControl.valueChanges.pipe(
			untilDestroyed(this),
			tap((value) => {
				this.calendarComponent.getApi().refetchResources();
			})
		).subscribe()
	}


	ngAfterViewInit(): void {

		setTimeout(() => {

			this.showCalendar = true;
			this.cdr.detectChanges();

		}, 100);

		this.calendars.changes
			.pipe(
				untilDestroyed(this),
				filter(list => !!list?.length),
				tap((list: QueryList<FullCalendarComponent>) => {

					setTimeout(() => {
						list.first?.getApi()?.updateSize();
						this.cdr.markForCheck();
					}, 100);
				})
			)
			.subscribe();


	}

	private getCalendarOptions(resources: AccountEntity[], timezone: string, hours: any, selectedAllocation: Allocation): CalendarOptions {

		const slotDurations = {
			'15': '00:15:00',
			'30': '00:30:00',
			'60': '01:00:00',
			'120': '02:00:00'
		};

		const snapDurations = {
			'15': '00:15:00',
			'30': '00:30:00',
			'60': '00:30:00',
			'120': '00:30:00'
		};

		const safeSlotDuration = '30';

		const snapDuration = snapDurations[safeSlotDuration];
		const slotDuration = slotDurations[safeSlotDuration];


		const calendarOptions: CalendarOptions = {
			schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives',
			editable: true,
			eventResourceEditable: true,
			selectable: true,
			unselectAuto: false,

			resourceAreaWidth: 260,
			resourceOrder: 'groupId, title',
			resourceGroupField: 'groupId',

			initialView: 'view',
			initialDate: selectedAllocation?.startDate,
			headerToolbar: false,
			height: '100%',

			locale: enAuLocale,
			timeZone: timezone,
			forceEventDuration: true,

			plugins: [
				luxonPlugin,
				resourceTimelinePlugin,
				resourceTimeGridPlugin,
				resourceDayGridPlugin,
				dayGridPlugin,
				interactionPlugin,
				scrollGridPlugin
			],

			resources: (e) => this.getCalendarResources.bind(this)(resources, hours, timezone),

			resourceAreaHeaderClassNames: () => 'resource-header',


			/// This function is used to enable / disable dragging of events. 
			/// prevent events from select to other day
			selectAllow: (e) => this.selectAllow.bind(this)(e),

			select: (e) => this.select.bind(this)(e),


			events: (e) => this.events.bind(this)(e),

			/// Called after an event has been modified in some way.
			/// This fires after any Event Object setter method is called, like setStart. 
			/// It is also called after an event has been dragged or resized. eventChange will fire after eventDrop and eventResize. 
			eventChange: (e) => this.eventChange.bind(this)(e),

			/// Triggered when resizing stops and the event has changed in duration.
			eventResize: (e) => this.eventResize.bind(this)(e),

			views: {
				view: {
					dayCount: 3,
					timeZone: timezone,
					type: 'resourceTimeline',
					nowIndicator: true,
					firstDay: hours.firstDay,
					hiddenDays: hours.hiddenDays,
					businessHours: hours.businessHours ?? true,
					slotMinTime: hours.slotMinTime,
					slotMaxTime: hours.slotMaxTime,
					slotDuration: slotDuration,
					snapDuration: snapDuration,

				}
			},
		};

		return calendarOptions;
	}

	private selectAllow(info: DateSpanApi) {

		const { selectedAllocation, timezone } = this.vm;

		/// Edit mode
		if (!!selectedAllocation) return false;

		/// Add mode
		const getMilliseconds = (date: Date) =>
			DateUtility.getDateTime(date, timezone).startOf('day').toMillis();

		const startDate = getMilliseconds(info.start);
		const endDate = getMilliseconds(info.end);

		return startDate === endDate;
	}

	private select(e: DateSelectArg) {
		const timezone = this.calendarComponent.getApi().getOption('timeZone');

		const start = DateTime.fromJSDate(e.start).setZone(timezone);
		const end = DateTime.fromJSDate(e.end).setZone(timezone);

		const resource = e?.resource?.extendedProps?.entity;

		this.selectTimeRange.emit({ start, end, resource });
	}

	private events(info): Promise<AllocationEventData[]> {

		const pr: Promise<AllocationEventData[]> = this.appointmentsService.getForPeriod(info.start, info.end, this.vm.selectedAllocation)
			// .then(x => new Promise(resolve => setTimeout(() => resolve(x), 1000)))
			.then(x => {
				this.calendarComponent.getApi().updateSize();

				setTimeout(() => {

					const el = document.getElementsByClassName('selected')?.[0];
					el?.scrollIntoView({ block: 'center' });
					// console.log('scroll to view', el, x)
				}, 100);

				return x as AllocationEventData[];
			});

		return pr;


	}

	private eventResize(e: EventResizeDoneArg) {

		const { event: { start, end, extendedProps } } = e;
		const timezone = this.calendarComponent.getApi().getOption('timeZone');

		///  Don't allow an event to be longer than 24hours
		const startDate = DateUtility.getDateTime(start, timezone);
		const endDate = DateUtility.getDateTime(end, timezone);

		// check that the appointmenmt is not longer than 24 hours
		const diff = endDate.diff(startDate, 'days').days;
		const valid = diff <= 1;;
		if (!valid) {
			e.revert();
			this.addAllocationRevert(extendedProps?.entity?._id);
		}
	}

	private eventChange(e: EventChangeArg) {

		const { event: { extendedProps } } = e;

		const allocationId = extendedProps?.entity?._id;

		if (this.isAllocationReverted(allocationId)) {
			this.removeAllocationRevert(allocationId);
			return;
		}


		const timezone = this.calendarComponent.getApi().getOption('timeZone');

		const start = DateTime.fromJSDate(e.event.start).setZone(timezone);
		const end = DateTime.fromJSDate(e.event.end).setZone(timezone);

		const resource = Enumerable.from(e.event.getResources()).firstOrDefault()?.extendedProps?.entity;

		this.selectTimeRange.emit({ start, end, resource });

	}

	private getHours(settings: SchedulingSettings): any {
		const dayMap = {
			Mon: 1,
			Tue: 2,
			Wed: 3,
			Thu: 4,
			Fri: 5,
			Sat: 6,
			Sun: 0
		};



		const daysOfWeek = Enumerable
			.from(settings?.workWeek ?? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'])
			.select(d => dayMap[d])
			.toArray();

		const firstDay = Enumerable.from(daysOfWeek).min();

		const hiddenDays = Enumerable
			.from([0, 1, 2, 3, 4, 5, 6])
			.where(x => !daysOfWeek.includes(x)).toArray();

		const businessHours = {
			startTime: settings?.dayStartTime ?? '09:00:',
			endTime: settings?.dayEndTime ?? '17:00',
			daysOfWeek,
		};

		const startComponents = Enumerable
			.from(businessHours.startTime.split(':'))
			.select(x => parseInt(x))
			.toArray();

		const endComponents = Enumerable
			.from(businessHours.endTime.split(':'))
			.select(x => parseInt(x))
			.toArray();

		const bs = DateTime
			.local()
			.set({ hour: startComponents[0], minute: startComponents[1] });

		const be = DateTime
			.local()
			.set({ hour: endComponents[0], minute: endComponents[1] });

		const dayStart = settings?.dayStartTime ?? '09:00:00';
		const dayEnd = settings?.dayEndTime ?? '18:00:00';

		const slotMinTime = Enumerable
			.from(dayStart.split(':'))
			.select((x, i) => {
				if (i == 0) {
					let hours = parseInt(x);
					if (hours >= 1) {
						hours -= 1;
					} else {
						return '00';
					}
					return hours.toString();
				}
				return x;
			})
			.toArray()
			.join(':');

		const slotMaxTime = Enumerable
			.from(dayEnd.split(':'))
			.select((x, i) => {
				if (i == 0) {
					let hours = parseInt(x);
					if (hours <= 22) {
						hours += 1;
					} else {
						hours = 23;
					}
					return hours.toString();
				}
				return x;
			})
			.toArray()
			.join(':');

		return {
			bs,
			be,
			businessHours,
			hiddenDays,
			firstDay,
			slotMinTime,
			slotMaxTime,
			startComponents,
			endComponents
		};
	}

	private getCalendarResources(inputResources: AccountEntity[], hours: any, timezone: string, successCallback, failureCallback) {

		return new Promise<SchedulerContactSummary[]>((resolve, reject) => {

			let resources: AccountEntity[] = this.resourceFilterControl.value;

			if (!resources?.length) {
				resources = inputResources;
			}

			try {

				const resourcesSummaries = Enumerable
					.from(resources)
					.select(contact => {

						const sc: SchedulerContactSummary = {
							id: contact._id,
							title: contact.name,
							timezone: timezone,
							businessHours: null,
							groupId: contact.type,
							entity: {
								_id: contact._id,
								name: contact.name,
								type: contact.type,
								active: true
							}
						};

						return sc;
					})
					.toArray();


				const calendarResources = Enumerable.from(resourcesSummaries)
					.select(resource => {
						if (!resource.timezone)
							return resource;

						const rs = hours.bs.setZone(resource.timezone, { keepLocalTime: true }).setZone(timezone);
						const re = hours.be.setZone(resource.timezone, { keepLocalTime: true }).setZone(timezone);

						if ((rs.hour > re.hour) && (rs.day !== re.day)) {
							return resource;
						}

						resource.businessHours = {
							...hours.businessHours,
							startTime: rs.toFormat('HH:mm'),
							endTime: re.toFormat('HH:mm'),

						};

						return resource;

					})
					.toArray();

				resolve(calendarResources);

			} catch (error) {
				reject(error);
			}

		})

	}

	private isAllocationReverted(allocationId: string) {
		return this._revertChangeAllocationIds.includes(allocationId);
	}

	private removeAllocationRevert(allocationId: string) {
		if (!allocationId) return;
		this._revertChangeAllocationIds = this._revertChangeAllocationIds.filter(id => id !== allocationId);
	}

	private addAllocationRevert(allocationId: string) {
		if (!allocationId) return;
		this._revertChangeAllocationIds.push(allocationId);
	}

}
